"use client";

import { useIsDesktop } from "@/hooks/use-media-query";
import type { InAppInfo } from "@/lib/check-device.server";
import { createContext, useContext, useEffect, useState, useMemo } from "react";
// OPTIMIZED: Removed ua-parser-js (~22KB savings)
// Using native user agent detection instead

interface DeviceContextType {
  isDesktop: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  inAppInfo: InAppInfo;
  isHydrated: boolean;
}

const DeviceContext = createContext<DeviceContextType | null>(null);

export function useDeviceContext() {
  const context = useContext(DeviceContext);

  if (!context) {
    throw new Error("useDeviceContext must be used within a DeviceProvider");
  }
  return context;
}

/**
 * Parse user agent string to detect device info using native regex
 * OPTIMIZED: Replaced ua-parser-js (~22KB savings) with native detection
 * Runs on client side to avoid blocking server rendering
 */
function parseDeviceInfo(
  userAgent: string,
): Omit<DeviceContextType, "isHydrated"> {
  const ua = userAgent.toLowerCase();

  // Detect mobile devices
  const isMobile = /mobile|iphone|ipod|android.*mobile|windows phone/i.test(ua);

  // Detect tablets
  const isTablet =
    /tablet|ipad|android(?!.*mobile)|kindle|silk/i.test(ua) ||
    (/macintosh/i.test(ua) && "ontouchend" in document); // iPad Pro detection

  // Detect Android
  const isAndroid = /android/i.test(ua);

  // Simple in-app browser detection
  const isInApp = /instagram|fban|fbav|linkedin|whatsapp|telegram/i.test(ua);
  const name = isInApp
    ? userAgent.match(/(Instagram|LinkedIn|WhatsApp|Telegram)/i)?.[0] ||
      "In-App Browser"
    : null;

  return {
    isDesktop: !isTablet && !isMobile,
    isAndroid,
    isMobile: isMobile || isTablet,
    inAppInfo: { isInApp, name },
  };
}

export function DeviceContextProvider({
  children,
  isDesktop: initialIsDesktop,
  isAndroid: initialIsAndroid,
  isMobile: initialIsMobile,
  inAppInfo: initialInAppInfo,
}: Omit<DeviceContextType, "isHydrated"> & { children: React.ReactNode }) {
  const [hydratedDeviceInfo, setHydratedDeviceInfo] = useState<{
    isDesktop: boolean;
    isAndroid: boolean;
    isMobile: boolean;
    inAppInfo: InAppInfo;
  } | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  const isMediaQueryDesktop = useIsDesktop();

  useEffect(() => {
    // Parse user agent on client side after hydration
    const userAgent = navigator.userAgent;
    const deviceInfo = parseDeviceInfo(userAgent);
    setHydratedDeviceInfo(deviceInfo);
    setIsHydrated(true);
  }, []);

  // Use media query as the source of truth for desktop detection
  const isDesktopClient =
    isMediaQueryDesktop !== undefined
      ? isMediaQueryDesktop
      : (hydratedDeviceInfo?.isDesktop ?? initialIsDesktop);

  const contextValue = useMemo(
    () => ({
      isDesktop: isDesktopClient,
      isAndroid: hydratedDeviceInfo?.isAndroid ?? initialIsAndroid,
      isMobile: hydratedDeviceInfo?.isMobile ?? initialIsMobile,
      inAppInfo: hydratedDeviceInfo?.inAppInfo ?? initialInAppInfo,
      isHydrated,
    }),
    [
      isDesktopClient,
      hydratedDeviceInfo,
      initialIsAndroid,
      initialIsMobile,
      initialInAppInfo,
      isHydrated,
    ],
  );

  return (
    <DeviceContext.Provider value={contextValue}>
      {children}
    </DeviceContext.Provider>
  );
}
